import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudUploadIcon, Mail } from 'lucide-react'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import IrsDocumentRow from './IrsDocumentRow'

export default function IrsCorrespondenceUpload({ applicationId }) {
  const navigate = useNavigate()
  const [documents, setDocuments] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  const documentType = 'irs-correspondence'

  useEffect(() => {
    handleGetDocuments()
  }, [])

  const handleGetDocuments = async () => {
    try {
      const response = await fetch(`${apiURL}/api/applications/${applicationId}/documents?type=${documentType}`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const data = await response.json()
      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken) await handleGetDocuments()
          else navigate('/', { state: { expired: true } })
        } else
          console.error(data.response.error)
      } else
        setDocuments(data.response.documents)
    } catch (error) {
      console.error(error)
    }
  }

  const handleUploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const uploadResponse = await fetch(`${apiURL}/api/applications/${applicationId}/documents?type=${documentType}`, {
        method: 'POST',
        headers: {
          'Authorization': account.accessToken
        },
        body: formData
      })

      const uploadData = await uploadResponse.json()
      if (uploadData.response.error) {
        if (uploadData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleUploadFile(file) : navigate('/', { state: { expired: true } })
        } else
          console.error(uploadData.response.error)
      } else {
        setDocuments(prev => [...prev, {
          documentId: uploadData.response.documentId,
          applicationId: applicationId,
          name: file.name,
          size: file.size,
          mimetype: file.type,
          documentURL: uploadData.response.documentURL
        }])
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleFileUpload = async (files) => {
    if (!files) return
    setIsUploading(true)
    const fileArray = Array.from(files)
    for (const file of fileArray) {
      await handleUploadFile(file)
    }
    setIsUploading(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    handleFileUpload(e.dataTransfer.files)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  return (
    <div className='w-full mt-7'>
      <div className='flex items-center gap-3 mb-4'>
        <div className='w-10 h-10 rounded-full bg-[#1C3E6B] flex items-center justify-center'>
          <Mail size={20} className='text-white' />
        </div>
        <div>
          <p className='text-xl font-semibold text-slate-800 dark:text-gray-100'>IRS Correspondence</p>
          <p className='text-sm text-gray-500'>Upload any letters, notices, or documents you receive from the IRS</p>
        </div>
      </div>

      <div className='w-full bg-white rounded-xl shadow-md p-6'>
        <label
          htmlFor='irs-file-upload'
          className='w-full border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition cursor-pointer'
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <CloudUploadIcon size={25} className='text-gray-400 mb-2' />
          <p className='text-lg font-semibold text-gray-700 mb-1'>
            {isUploading ? 'Uploading...' : 'Upload IRS Documents'}
          </p>
          <p className='text-sm text-gray-500'>
            Drag and drop files here or click to upload
          </p>
        </label>
        <input
          id='irs-file-upload'
          type='file'
          multiple
          className='hidden'
          onChange={(e) => handleFileUpload(e.target.files)}
        />

        <div className='mt-4'>
          {documents.length === 0 ? (
            <p className='text-gray-500 text-center py-4'>
              No IRS correspondence uploaded yet.
            </p>
          ) : (
            <ul className='space-y-3'>
              {documents.map(doc => (
                <IrsDocumentRow
                  key={doc.documentId}
                  applicationId={applicationId}
                  _document={doc}
                  setDocuments={setDocuments}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
